import fontkit from '@pdf-lib/fontkit';
import { useEffect, useSyncExternalStore } from 'react';
import type { FontProvider, FontkitLikeFont, LoadedFont } from '@cal/engine';
import { fontMeta, useLibrary } from './library';

/**
 * Loads the exact font files used for export, parses them with the same
 * fontkit build as the server, and exposes glyph outlines for SVG rendering.
 */
class BrowserFonts implements FontProvider {
  private loaded = new Map<string, LoadedFont>();
  private pending = new Map<string, Promise<void>>();
  readonly errors = new Map<string, string>();
  private glyphs = new Map<string, string>();
  private listeners = new Set<() => void>();
  version = 0;

  get(id: string) {
    return this.loaded.get(id);
  }
  isLoaded(id: string) {
    return this.loaded.has(id);
  }
  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  private bump() {
    this.version++;
    this.listeners.forEach((l) => l());
  }

  ensure(ids: string[]): Promise<void> {
    const fonts = useLibrary.getState().fonts;
    const jobs = ids.filter((id) => !this.loaded.has(id)).map((id) => {
      let p = this.pending.get(id);
      if (p) return p;
      const info = fonts.find((f) => f.id === id);
      if (!info) return Promise.resolve(); // unknown → layout reports "font missing"
      p = fetch(`/api/fonts/${encodeURIComponent(id)}/file`, { credentials: 'same-origin' })
        .then(async (r) => {
          if (!r.ok) throw new Error(r.status === 410 ? 'font file missing on server' : `HTTP ${r.status}`);
          const font = fontkit.create(new Uint8Array(await r.arrayBuffer())) as unknown as FontkitLikeFont;
          this.loaded.set(id, { meta: fontMeta(info), font });
          this.errors.delete(id);
        })
        .catch((e) => { this.errors.set(id, String(e.message ?? e)); })
        .finally(() => { this.pending.delete(id); this.bump(); });
      this.pending.set(id, p);
      return p;
    });
    return Promise.all(jobs).then(() => undefined);
  }

  isPending(ids: string[]) {
    return ids.some((id) => this.pending.has(id));
  }

  /** SVG path (font units, y up) for a glyph — cached. */
  glyphPath(fontId: string, gid: number): string {
    const key = `${fontId}:${gid}`;
    let d = this.glyphs.get(key);
    if (d === undefined) {
      const f = this.loaded.get(fontId);
      d = f ? f.font.getGlyph(gid).path.toSVG() : '';
      this.glyphs.set(key, d);
    }
    return d;
  }
}

export const browserFonts = new BrowserFonts();

/** Ensure fonts are loaded; re-renders when any font finishes loading. */
export function useFonts(ids: string[]): { version: number; loading: boolean } {
  const version = useSyncExternalStore(browserFonts.subscribe, () => browserFonts.version);
  const libFonts = useLibrary((s) => s.fonts);
  const key = ids.join('|');
  useEffect(() => {
    void browserFonts.ensure(ids);
  }, [key, libFonts]); // eslint-disable-line react-hooks/exhaustive-deps
  return { version, loading: browserFonts.isPending(ids) || ids.some((id) => !browserFonts.isLoaded(id) && !browserFonts.errors.has(id) && libFonts.some((f) => f.id === id)) };
}
